import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import type {
  DeliveryTrackerDocument,
  DeliveryTrackerDocumentActionsInputs,
} from '@features/organization/components/delivery-tracker-detail/types/delivery-tracker-document.types';
import { DeliveryTrackerDocumentDeleteStateMap } from '@features/organization/components/delivery-tracker-detail/utility/delivery-tracker-document-delete.utility';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  ButtonComponent,
  ButtonVariantMap,
  CUSTOM_INPUTS,
  IconNameMap,
  ROW_DATA,
} from '@kerberos-compliance/compliance-shared-ui';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'aad-delivery-tracker-document-actions',
  imports: [ButtonComponent, NzTooltipDirective, TranslocoDirective],
  templateUrl: 'delivery-tracker-document-actions.component.html',
  styleUrl: 'delivery-tracker-document-actions.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryTrackerDocumentActionsComponent {
  protected readonly ButtonVariantMap = ButtonVariantMap;

  protected readonly IconNameMap = IconNameMap;

  private readonly document = inject<DeliveryTrackerDocument>(ROW_DATA);

  private readonly actions = inject<DeliveryTrackerDocumentActionsInputs>(CUSTOM_INPUTS);

  private readonly deleteState = computed(() => this.actions.deleteState(this.document));

  protected readonly canOfferDelete = computed(
    () => this.deleteState() !== DeliveryTrackerDocumentDeleteStateMap.hidden,
  );

  protected readonly isLastRiskAnalysis = computed(
    () => this.deleteState() === DeliveryTrackerDocumentDeleteStateMap.lastRiskAnalysis,
  );

  protected readonly isDeleteDisabled = computed(
    () => this.isLastRiskAnalysis() || this.actions.isDeleting(this.document),
  );

  /**
   * Downloads this row's document.
   *
   * @returns {void} This method does not return a value.
   */
  protected download(): void {
    this.actions.download(this.document);
  }

  /**
   * Starts deleting this row's document.
   *
   * @returns {void} This method does not return a value.
   */
  protected delete(): void {
    if (this.isDeleteDisabled()) {
      return;
    }
    this.actions.delete(this.document);
  }
}
